import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useMap } from 'react-leaflet';
import { MapPin, Search } from 'lucide-react';

// 🎯 بيقبل "30.0444, 31.2357" أو "30.0444,31.2357" - مساحات اختيارية حوالين الفاصلة
const parseCoordinates = (query) => {
  const match = query.trim().match(/^(-?\d+(\.\d+)?)\s*,\s*(-?\d+(\.\d+)?)$/);
  if (!match) return null;
  const lat = parseFloat(match[1]);
  const lng = parseFloat(match[3]);
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
};

// 🎯 لازم يترسم جوه <MapContainer> - Leaflet مش بيعيد تركيز الكاميرا
// أوتوماتيك لما الـ center prop يتغيّر بعد أول رسمة، فمحتاجين map.flyTo
// صريح كل ما flyToCoords تتغيّر (نفس الحل المستخدم في OrganizationDetails.js)
export const FlyToHandler = ({ flyToCoords, zoom = 18 }) => {
  const map = useMap();
  useEffect(() => {
    if (flyToCoords) {
      map.flyTo(flyToCoords, zoom, { animate: true, duration: 1.2 });
    }
  }, [flyToCoords, map, zoom]);
  return null;
};

// 🎯 مربع بحث عايم فوق الخريطة - لازم يترسم كـ sibling لـ MapContainer
// (مش جواها) جوه نفس الـ wrapper اللي عليه position:relative، مش جوه
// MapContainer نفسها. بيدعم البحث النصي (Nominatim) وكمان كتابة
// إحداثيات مباشرة "lat, lng"
export const MapSearchBox = ({ onSelect, placeholder }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);

  useEffect(() => {
    const coords = parseCoordinates(query);
    if (coords) {
      setResults([{ lat: coords.lat, lon: coords.lng, display_name: `${coords.lat}, ${coords.lng}` }]);
      return;
    }

    const delay = setTimeout(async () => {
      if (query.trim().length > 2) {
        try {
          const res = await axios.get(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`);
          setResults(res.data || []);
        } catch (err) {
          console.error("Map search error:", err);
        }
      } else {
        setResults([]);
      }
    }, 500);
    return () => clearTimeout(delay);
  }, [query]);

  const handlePick = (r) => {
    onSelect(parseFloat(r.lat), parseFloat(r.lon), r.display_name);
    setQuery('');
    setResults([]);
  };

  return (
    <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] w-[380px] max-w-[90%] flex flex-col gap-1">
      <div className="flex items-center bg-surface-card/90 backdrop-blur-sm shadow-lg rounded-lg border border-line overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 transition-all">
        <Search size={14} className="text-ink-secondary mx-3 shrink-0" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="flex-1 py-2.5 pe-3 outline-none bg-transparent font-bold text-ink text-sm"
          dir="auto"
        />
      </div>
      {results.length > 0 && (
        <div className="bg-surface-card/95 backdrop-blur-md shadow-xl rounded-lg border border-line max-h-[250px] overflow-y-auto custom-scrollbar">
          {results.map((r, idx) => (
            <div
              key={idx}
              onClick={() => handlePick(r)}
              className="flex items-start gap-3 p-3 border-b border-line-subtle last:border-0 hover:bg-primary-soft cursor-pointer transition-colors"
            >
              <MapPin size={16} className="text-status-warning mt-0.5 shrink-0" />
              <span className="text-xs font-bold text-ink leading-relaxed line-clamp-2 w-full">
                {r.display_name}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MapSearchBox;
